import { useDebounce, useEffectOnMount, useEffectOnUpdate, useToggle } from '@old/hooks/core';
import { useService } from '@old/hooks/use-service';
import {
  Modal,
  FormControl,
  Button,
  Divider,
  Fade,
  FormControlLabel,
  FormLabel,
  RadioGroup,
  TextField,
  Radio,
  TextareaAutosize,
  Typography,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Chip,
  CircularProgress,
  styled,
  Tooltip,
} from '@mui/material';
import VerifiedIcon from '@mui/icons-material/Verified';
import { Box, Container, Stack } from '@mui/system';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import { configButtonStylesContained, stylesInput } from '@old/styles/generic-styles';
import { ChangeEvent, PropsWithChildren, SyntheticEvent, useEffect, useMemo, useRef, useState } from 'react';
// import es from 'dayjs/locale/es';
import i18n from '../../../../libs/language';
import { ToastAction } from '@old/ui/toast/toast-action';
import { listSilobagService } from '@old/services/domain/base/silobag/base/silobag-list';
import { useGlobalStore } from '@old/global-store/use-store';
import { silobagDataDetailService } from '@old/services/domain/base/silobag/base/silobag-data-detail';
import { createMultipleExtractionService } from '@old/services/domain/base/silobag/extraction/extraction-multiple-creation';
import { createExtractionService } from '@old/services/domain/base/silobag/extraction/extraction-creation';
import { CreateExtractionBody } from '@old/services/domain/base/silobag/extraction/extraction-creation.types';
import { AutocompleteInput } from '@old/ui/autocomplete/autocomplete';
import { searchExtractionFieldsService } from '@old/services/silobag_extraction/silobag-extraction-predictive';
import { uploadPdfService } from '@old/services/domain/base/silobag/extraction/upload-pdf';
import { PdfViewer } from '@old/ui/pdf-viewer/pdf-viewer';
import { updateExtractionService } from '@old/services/domain/base/silobag/extraction/extraction-edition';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { WarningModal } from './warning-modal';
import { MonitoredWarningExtractionModal } from './monitored-warning-extraction-modal';
import { listMonitoredSilobagProtocols } from '@old/services/domain/base/silobag/base/silobag-protocol';
import * as HOOKS from '@siloreal:shared:hooks';

type SummarySilobagList = Awaited<ReturnType<typeof listSilobagService>>['summary']['data'];

type CreateExtractionService = Awaited<ReturnType<typeof createExtractionService>>['summary'];

type SilobagDetailParams = Parameters<typeof silobagDataDetailService>[0];

type ExtractionModalProps = {
  open: boolean;
  fromSilobag?: boolean;
  setOpen: (open: boolean) => void;
  siloName?: string | undefined;
  silobagOrExtractionId?: string | number | null | undefined;
  remainingStock?: string | null | number;
  type: 'new' | 'view' | 'edit';
  extractionData?: Partial<CreateExtractionService>;
  fromHistory?: boolean;
  silobagsList?: SummarySilobagList;
  setSnackbarMessage?: (message: string) => void;
  setTypeSnackbar?: (type: 'success' | 'error') => void;
  setOpenSnackbar?: (setOpen: boolean) => void;
  enableEditMode?: () => void;
  setRemainingStock?: (data: string | null | number) => void;
  writePermission?: boolean;
  siloTrigger?: (query: SilobagDetailParams) => void;
  setExtractionSuccess?: (success: boolean) => void;
  selectedSilobags?: SummarySilobagList;
  active?: boolean;
  acceptProtocolInvalidation?: boolean;
  lockSilobagSelection?: boolean;
};

type ModalProps = {
  open: boolean;
  isErrorArcaPdf: boolean;
  onClose: () => void;
  pdfView: File | null;
};

// const locales = { es: es };
// type LocaleKey = keyof typeof locales;
type LocaleKey = 'es';

type InputChg = ChangeEvent<HTMLInputElement>;
type TextAreaChg = ChangeEvent<HTMLTextAreaElement>;

type MultipleSilosDataProps = {
  weigthSilo1: string | undefined;
  weigthSilo2: string | undefined;
  humiditySilo1: string | undefined;
  humiditySilo2: string | undefined;
  nameSilo1: string | undefined;
  nameSilo2: string | undefined;
  idSilo1?: string | undefined;
  idSilo2?: string | undefined;
};

type ErrorData = {
  silobag_name: string;
  weight: string;
  extraction_date: string;
  gross_weight: string;
  truck_weight: string;
  silo_groupal_extraction_1: string;
  silo_groupal_extraction_2: string;
  humiditySilo1: string;
  humiditySilo2: string;
  humidity: string;
};

type Handler = {
  silobag_name: (event: SelectChangeEvent<string[]>) => void;
  extraction_date: (e: Dayjs | null) => void;
  weight: (e: InputChg) => void;
  observations: (event: TextAreaChg) => void;
  gross_weight: (e: InputChg) => void;
  truck_weight: (e: InputChg) => void;
  freighTransport: (e: InputChg) => void;
  carrier: (e: InputChg) => void;
  backPlate: (e: InputChg) => void;
  driver: (e: InputChg) => void;
  trailerBackPlate: (e: InputChg) => void;
  ctgDocument: (e: InputChg) => void;
  waybill: (e: InputChg) => void;
  destination: (e: InputChg) => void;
  contract: (e: InputChg) => void;
  humidity: (e: InputChg) => void;
  silo_group_1: (e: InputChg) => void;
  silo_group_2: (e: InputChg) => void;
  humiditySilo1: (e: InputChg) => void;
  humiditySilo2: (e: InputChg) => void;
};

type ExtractionValues = {
  extraction_date?: CreateExtractionService['extraction_date'] | string;
  weight?: CreateExtractionService['weight'];
  observations?: CreateExtractionService['observations'];
  gross_weight?: CreateExtractionService['gross_weight'];
  truck_weight?: CreateExtractionService['truck_weight'];
  humidity?: CreateExtractionService['humidity'];
  freight_transport?: CreateExtractionService['freight_transport'];
  carrier?: CreateExtractionService['carrier'];
  back_plate?: CreateExtractionService['back_plate'];
  driver?: CreateExtractionService['driver'];
  trailer_back_plate?: CreateExtractionService['trailer_back_plate'];
  ctg_document?: CreateExtractionService['ctg_document'];
  waybill?: CreateExtractionService['waybill'];
  destination?: CreateExtractionService['destination'];
  contract?: CreateExtractionService['contract'];
  type?: CreateExtractionService['type'];
};

dayjs.extend(utc);
dayjs.locale('es');

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

function MonitoredSilobagIcon({
  silobagId,
  monitoredSilobagIds,
}: {
  silobagId: string;
  monitoredSilobagIds: Set<string>;
}) {
  if (!monitoredSilobagIds.has(silobagId)) return null;

  return (
    <Tooltip title={i18n.t('app.siloreal_with_vrr')}>
      <span style={{ display: 'flex', alignItems: 'center' }}>
        <VerifiedIcon sx={{ color: '#68BD00', fontSize: 16 }} />
      </span>
    </Tooltip>
  );
}

// TODO: CAMBIAR NOMBRE DEL ARCHIVO
export function ExtractionAction({
  open,
  setOpen,
  siloName,
  silobagOrExtractionId,
  remainingStock,
  setSnackbarMessage,
  setTypeSnackbar,
  setOpenSnackbar,
  type,
  enableEditMode,
  extractionData,
  silobagsList,
  fromSilobag = false,
  fromHistory = false,
  setRemainingStock,
  writePermission = true,
  setExtractionSuccess,
  selectedSilobags,
  active = true,
  acceptProtocolInvalidation = false,
  lockSilobagSelection = false,
}: ExtractionModalProps) {
  const initErrorData: ErrorData = {
    silobag_name: '',
    weight: '',
    extraction_date: '',
    gross_weight: '',
    truck_weight: '',
    silo_groupal_extraction_1: '',
    silo_groupal_extraction_2: '',
    humiditySilo1: '',
    humiditySilo2: '',
    humidity: '',
  };

  const TRUCK_ID = 1;
  const CONSOLIDATED_ID = 2;

  const exType =
    type === 'edit' || type === 'view'
      ? extractionData?.type === 'truck'
        ? TRUCK_ID
        : CONSOLIDATED_ID
      : TRUCK_ID;

  const locale: LocaleKey = 'es';
  const [disableSave, setDisableSave] = useToggle(false);
  const [extractionType, setExtractionType] = useState(exType);
  const [errorData, setErrorData] = useState<ErrorData>(initErrorData);
  const [idToUse, setIdToUse] = useState<number | string[] | string>(silobagOrExtractionId || []);
  const readonly = type === 'view';
  const [silobagExtraction, setSilobagExtraction] = useState<string[]>(siloName ? [siloName] : []);
  const [silobagName, setSilobagName] = useState(siloName || '');
  const [isMultipleExtraction, setIsMultipleExtraction] = useToggle(false);
  const [errorExtractionMsg, setErrorExtractionMsg] = useState('');
  const [isInvalidFileType, setIsInvalidFileType] = useState(false);
  const [pdfView, setPdfView] = useState<File | null>(null);
  const [openWarningModal, setOpenWarningModal] = useState(false);
  const [openMonitoredWarningModal, setOpenMonitoredWarningModal] = useState(false);
  const [monitoredSilobagsToAlert, setMonitoredSilobagsToAlert] = useState<string[]>([]);
  const [protocolInvalidationAccepted, setProtocolInvalidationAccepted] =
    useState(acceptProtocolInvalidation);
  const [monitoredSilobagIds, setMonitoredSilobagIds] = useState<Set<string>>(new Set());
  const { filters, organizationId } = useGlobalStore();
  const [manualWeightInput, setManualWeightInput] = useToggle(false);

  const queryParams = {
    active: true,
    silobag_data_status_id: filters.silobag_data_status_id,
    organization_id: organizationId,
  };

  useEffectOnMount(() => {
    if (selectedSilobags && selectedSilobags.length <= 2 && selectedSilobags.length > 0) {
      if (selectedSilobags.length === 2) setIsMultipleExtraction(true);
      setSilobagName(selectedSilobags[0].name);
      const selectedValues = selectedSilobags.map((obj) => obj.id);
      setSilobagExtraction(selectedValues);
      setIdToUse(selectedValues);
      const selectedSilobag1 = selectedSilobags?.find((silobag) => silobag.id === selectedValues[0]);
      setSilobagGrupalExtraction((prevState) => ({
        ...prevState,
        nameSilo1: selectedSilobag1?.name ?? '―',
        idSilo1: selectedValues[0],
      }));
      const selectedSilobag2 = selectedSilobags?.find((silobag) => silobag.id === selectedValues[1]);
      setSilobagGrupalExtraction((prevState) => ({
        ...prevState,
        nameSilo2: selectedSilobag2?.name ?? '―',
        idSilo2: selectedValues[1],
      }));
    }
  });

  const {
    trigger: importArcaPdf,
    summary: importArcaSumm,
    isLoading: isLoadingArcaData,
    isError: isErrorArcaPdf,
  } = useService(uploadPdfService);

  const { summary: silosForExtraction, isLoading: isLoadingExportSilobagsList } = useService(
    listSilobagService,
    {
      fetchOnMount: [{ query: queryParams }],
    }
  );

  const findSilobagById = (id?: string) => {
    if (!id) return undefined;

    return (
      silobagsList?.find((silobag) => silobag.id === id) ??
      silosForExtraction?.data?.find((silobag) => silobag.id === id) ??
      selectedSilobags?.find((silobag) => silobag.id === id)
    );
  };

  const idToNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    const sources = [
      ...(silobagsList ?? []),
      ...(silosForExtraction?.data ?? []),
      ...(selectedSilobags ?? []),
    ];

    sources.forEach((silobag) => {
      map[silobag.id] = silobag.name;
    });

    if (typeof silobagOrExtractionId === 'string' && siloName) {
      map[silobagOrExtractionId] = siloName;
      map[siloName] = siloName;
    }

    return map;
  }, [silobagsList, silosForExtraction?.data, selectedSilobags, silobagOrExtractionId, siloName]);

  const getSilobagDisplayName = (value: string) => idToNameMap[value] ?? value;

  const resolveSilobagId = (value: string): string => {
    if (findSilobagById(value)) return value;

    if (
      typeof silobagOrExtractionId === 'string' &&
      (value === siloName || silobagExtraction.includes(value))
    ) {
      return silobagOrExtractionId;
    }

    return value;
  };
  // FOR MULTIPLE SILOBAGS
  const [silobagGrupalExtraction, setSilobagGrupalExtraction] = useState<MultipleSilosDataProps>({
    weigthSilo1: '',
    weigthSilo2: '',
    nameSilo1: '',
    nameSilo2: '',
    idSilo1: '',
    idSilo2: '',
    humiditySilo1: '',
    humiditySilo2: '',
  });

  const handleOnClose = () => {
    setRemainingStock && setRemainingStock(0);
    setOpenMonitoredWarningModal(false);
    setMonitoredSilobagsToAlert([]);
    setProtocolInvalidationAccepted(false);
    setOpen(false);
  };

  useEffect(() => {
    if (open) {
      setProtocolInvalidationAccepted(acceptProtocolInvalidation);
    }
  }, [open, acceptProtocolInvalidation]);

  useEffect(() => {
    if (!open) {
      setMonitoredSilobagIds(new Set());
      return;
    }

    const silobagIds = [
      ...(silosForExtraction?.data?.map((silobag) => silobag.id) ?? []),
      ...(silobagsList?.map((silobag) => silobag.id) ?? []),
      ...(selectedSilobags?.map((silobag) => silobag.id) ?? []),
      ...(typeof silobagOrExtractionId === 'string' ? [silobagOrExtractionId] : []),
    ].filter(Boolean);

    if (silobagIds.length === 0) return;

    let cancelled = false;

    const fetchMonitoredSilobags = async () => {
      try {
        const { summary } = await listMonitoredSilobagProtocols({
          query: { ids: [...new Set(silobagIds)] },
        });

        if (cancelled) return;

        setMonitoredSilobagIds(
          new Set(summary?.filter((item) => item.protocol != null).map((item) => item.silobag_id) ?? [])
        );
      } catch {
        if (!cancelled) {
          setMonitoredSilobagIds(new Set());
        }
      }
    };

    void fetchMonitoredSilobags();

    return () => {
      cancelled = true;
    };
  }, [open, silosForExtraction?.data, silobagsList, selectedSilobags, silobagOrExtractionId]);

  const getSelectedSilobagIds = (): string[] => {
    if (Array.isArray(idToUse)) {
      return idToUse.filter(Boolean).map(String);
    }

    if (typeof idToUse === 'string' && idToUse) {
      return [idToUse];
    }

    return silobagExtraction.filter(Boolean).map(resolveSilobagId);
  };

  const closeOnSuccess = () => {
    handleOnClose();
    setTypeSnackbar?.('success');
    if (type === 'new') setSnackbarMessage?.(i18n.t('app.silobag_detail.new_extraction'));
    if (type === 'edit') setSnackbarMessage?.(i18n.t('app.silobag_detail.edit_extraction'));

    setOpenSnackbar?.(true);
    setOpen(false);
    if (setExtractionSuccess) setExtractionSuccess(true);

    setTimeout(() => {
      if (fromSilobag) {
        setExtractionSuccess && setExtractionSuccess(false);
        setOpenSnackbar?.(false);
      }
    }, 3000);
  };

  const {
    trigger: triggerExtraction,
    isError: errorInExtraction,
    error: errorMsg,
    isLoading: isLoadingExtraction,
  } = useService(createExtractionService, {
    onSuccess: closeOnSuccess,
  });

  const {
    trigger: triggerMultipleExtraction,
    isError: errorInMultiExtraction,
    error: errorMultipleMsg,
    isLoading: isLoadingMultiExtraction,
  } = useService(createMultipleExtractionService, {
    onSuccess: closeOnSuccess,
  });

  const { trigger: triggerUpdateExtraction, isLoading: isLoadingUpdateExtraction } = useService(
    updateExtractionService,
    {
      onSuccess: closeOnSuccess,
    }
  );

  const parseWeight = (val?: string) => {
    return val ? parseInt(val) || 0 : 0;
  };

  const formatDate = (date?: string | Date): string => {
    if (!date) return dayjs().format('YYYY-MM-DD');

    if (date instanceof Date) return dayjs(date).format('YYYY-MM-DD');

    const parsedDate = dayjs(date, ['DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD', 'YYYY/MM/DD'], true);

    return parsedDate.isValid() ? parsedDate.format('YYYY-MM-DD') : dayjs(date).format('YYYY-MM-DD');
  };

  const isLoading = isLoadingExtraction || isLoadingMultiExtraction || isLoadingUpdateExtraction;
  const [extractionValues, setExtractionValues] = useState<ExtractionValues>({
    extraction_date: type !== 'new' ? extractionData?.extraction_date : '',
    weight: type !== 'new' ? (extractionData?.weight as string) : '',
    observations: type !== 'new' ? (extractionData?.observations as string) : '',
    gross_weight: type !== 'new' ? (extractionData?.gross_weight as string) : null,
    truck_weight: type !== 'new' ? (extractionData?.truck_weight as string) : null,
    freight_transport: type !== 'new' ? (extractionData?.freight_transport as string) : '',
    carrier: type !== 'new' ? (extractionData?.carrier as string) : '',
    back_plate: type !== 'new' ? (extractionData?.back_plate as string) : '',
    driver: type !== 'new' ? (extractionData?.driver as string) : '',
    trailer_back_plate: type !== 'new' ? (extractionData?.trailer_back_plate as string) : '',
    ctg_document: type !== 'new' ? (extractionData?.ctg_document as string) : '',
    waybill: type !== 'new' ? (extractionData?.waybill as string) : '',
    destination: type !== 'new' ? (extractionData?.destination as string) : '',
    contract: type !== 'new' ? (extractionData?.contract as string) : '',
    humidity: type !== 'new' ? (extractionData?.humidity as string) : null,
  });

  dayjs.extend(utc);
  dayjs.locale('es');
  dayjs.extend(customParseFormat);

  useEffectOnUpdate(() => {
    if (!importArcaSumm) return;

    const { response } = importArcaSumm;

    setExtractionValues((prevValues) => ({
      ...prevValues,
      extraction_date: formatDate(response?.form_values?.extraction_date),
      weight: response.form_values?.weight ?? '',
      gross_weight: response.form_values?.gross_weight ?? '',
      truck_weight: response.form_values?.truck_weight ?? '',
      freight_transport: response.form_values?.freight_transport ?? '',
      carrier: response.form_values?.carrier ?? '',
      back_plate: response.form_values?.backplate ?? '',
      driver: response.form_values?.driver ?? '',
      trailer_back_plate: response.form_values?.trailer_backplate ?? '',
      ctg_document: response.form_values?.ctg_document ?? '',
      waybill: response.form_values?.waybill ?? '',
      destination: response.form_values?.destination ?? '',
    }));
  }, [importArcaSumm]);

  useEffectOnUpdate(() => {
    const hasTaraAndGross = extractionValues.truck_weight && extractionValues.gross_weight;
    const tara = Math.max(0, parseInt((extractionValues.truck_weight as string) ?? '0'));
    const pesoBruto = Math.max(0, parseInt((extractionValues.gross_weight as string) ?? '0'));
    const weightSilo1 = Math.max(0, parseInt(silobagGrupalExtraction.weigthSilo1 ?? '0'));
    const weightSilo2 = Math.max(0, parseInt(silobagGrupalExtraction.weigthSilo2 ?? '0'));

    let newWeight = 0;

    if (hasTaraAndGross) {
      // Si hay peso bruto y tara, se calcula como peso bruto - tara
      newWeight = Math.max(0, pesoBruto - tara);

      // Autorrelleno de silobolsas
      if (silobagGrupalExtraction.weigthSilo1) {
        setSilobagGrupalExtraction((prev) => ({
          ...prev,
          weigthSilo2: Math.max(0, newWeight - weightSilo1).toString(),
        }));
      } else if (silobagGrupalExtraction.weigthSilo2) {
        setSilobagGrupalExtraction((prev) => ({
          ...prev,
          weigthSilo1: Math.max(0, newWeight - weightSilo2).toString(),
        }));
      }
    } else if (silobagGrupalExtraction.weigthSilo1 && silobagGrupalExtraction.weigthSilo2) {
      // Si no hay peso bruto ni tara, pero hay pesos de silobolsas, se suman
      newWeight = Math.max(0, weightSilo1 + weightSilo2);
    }

    if (newWeight > 0) {
      setExtractionValues((prevValues) => ({
        ...prevValues,
        weight: newWeight,
      }));
    }
  }, [
    extractionValues.truck_weight,
    extractionValues.gross_weight,
    silobagGrupalExtraction.weigthSilo1,
    silobagGrupalExtraction.weigthSilo2,
  ]);

  const rootDataError = (key: string, value: string) => setErrorData((prev) => ({ ...prev, [key]: value }));
  const rootDataChange = (key: string, value: string) =>
    setExtractionValues((prev) => ({ ...prev, [key]: value }));

  const VALIDATION = {
    ONLY_NUMS: (value: string) => !/^[0-9]*$/.test(value),
    BETWEEN_0_AND_100: (value: string) => /^(\d{0,2}|\d{0,2}[.]|\d{0,2}[.]\d{1,2}|100)$/.test(value),
  };

  const HANDLERS: Handler = {
    silobag_name: (event: SelectChangeEvent<string[]>) => {
      rootDataError('silobag_name', '');
      const selectedValues =
        typeof event.target.value === 'string' ? event.target.value.split(',') : event.target.value;

      if (selectedValues.length <= 2) {
        setSilobagExtraction(selectedValues);

        const selectedSilobag1 = findSilobagById(selectedValues[0]);
        setSilobagGrupalExtraction((prevState) => ({
          ...prevState,
          nameSilo1: selectedSilobag1?.name ?? '-',
          idSilo1: selectedValues[0],
        }));

        const selectedSilobag2 = findSilobagById(selectedValues[1]);
        setSilobagGrupalExtraction((prevState) => ({
          ...prevState,
          nameSilo2: selectedSilobag2?.name ?? '-',
          idSilo2: selectedValues[1],
        }));

        setSilobagName(selectedSilobag1?.name ?? '');
      }

      setIdToUse(selectedValues);
    },
    extraction_date: (e: Dayjs | null) => {
      if (!e) return;
      const input = dayjs(e.toString()).format('YYYY-M-D');
      rootDataError('extraction_date', '');
      rootDataChange('extraction_date', input);
      if (extractionValues.gross_weight && idToUse) {
        setDisableSave(false);
      }
    },
    weight: (e: InputChg) => {
      const input = e.target.value;
      if (VALIDATION.ONLY_NUMS(input)) {
        rootDataError('weight', 'Solo se aceptan números.');
        return;
      }
      rootDataError('weight', '');
      rootDataChange('weight', input);
    },
    observations: (event: TextAreaChg) => {
      const input = event.target.value;
      rootDataError('observations', '');
      rootDataChange('observations', input);
    },
    gross_weight: (e: InputChg) => {
      const input = e.target.value;
      if (VALIDATION.ONLY_NUMS(input)) {
        rootDataError('gross_weight', 'Solo se aceptan números.');
        return;
      }
      rootDataError('gross_weight', '');
      rootDataChange('gross_weight', input);
      if (extractionValues.extraction_date && idToUse) {
        setDisableSave(false);
      }
    },
    truck_weight: (e: InputChg) => {
      const input = e.target.value;
      if (VALIDATION.ONLY_NUMS(input)) {
        rootDataError('truck_weight', 'Solo se aceptan números.');
        return;
      }
      rootDataError('truck_weight', '');
      rootDataChange('truck_weight', input);
    },
    freighTransport: (e: InputChg) => {
      const input = e.target.value;
      rootDataChange('freight_transport', input);
    },
    carrier: (e: InputChg) => {
      const input = e.target.value;
      rootDataChange('carrier', input);
    },
    backPlate: (e: InputChg) => {
      const input = e.target.value;
      rootDataChange('back_plate', input);
    },
    humidity: (e: InputChg) => {
      const input = e.target.value;
      if (VALIDATION.ONLY_NUMS(input)) {
        rootDataError('humidity', 'Solo se aceptan números.');
        return;
      }

      if (!VALIDATION.BETWEEN_0_AND_100(input)) {
        rootDataError('humidity', 'Solo se aceptan números entre 0 y 100.');
        return;
      }
      rootDataError('humidity', '');
      rootDataChange('humidity', input);
    },
    driver: (e: InputChg) => {
      const input = e.target.value;
      rootDataChange('driver', input);
    },
    trailerBackPlate: (e: InputChg) => {
      const input = e.target.value;
      rootDataChange('trailer_back_plate', input);
    },
    ctgDocument: (e: InputChg) => {
      const input = e.target.value;
      rootDataChange('ctg_document', input);
    },
    waybill: (e: InputChg) => {
      const input = e.target.value;
      rootDataChange('waybill', input);
    },
    destination: (e: InputChg) => {
      const input = e.target.value;
      rootDataChange('destination', input);
    },
    contract: (e: InputChg) => {
      const input = e.target.value;
      rootDataChange('contract', input);
    },
    silo_group_1: (e: InputChg) => {
      const input = e.target.value;
      if (VALIDATION.ONLY_NUMS(input)) {
        rootDataError('silo_groupal_extraction_1', 'Solo se aceptan números.');
        return;
      }
      rootDataError('silo_groupal_extraction_1', '');
      setSilobagGrupalExtraction((prevData) => ({
        ...prevData,
        weigthSilo1: input,
      }));
    },
    silo_group_2: (e: InputChg) => {
      const input = e.target.value;
      if (VALIDATION.ONLY_NUMS(input)) {
        rootDataError('silo_groupal_extraction_2', 'Solo se aceptan números.');
        return;
      }
      rootDataError('silo_groupal_extraction_2', '');
      setSilobagGrupalExtraction((prevData) => ({
        ...prevData,
        weigthSilo2: input,
      }));
    },
    humiditySilo1: (e: InputChg) => {
      const input = e.target.value;
      if (VALIDATION.ONLY_NUMS(input)) {
        rootDataError('humiditySilo1', 'Solo se aceptan números.');
        return;
      }

      if (!VALIDATION.BETWEEN_0_AND_100(input)) {
        rootDataError('humiditySilo1', 'Solo se aceptan números entre 0 y 100.');
        return;
      }

      rootDataError('humiditySilo1', '');
      setSilobagGrupalExtraction((prevData) => ({
        ...prevData,
        humiditySilo1: input,
      }));
    },
    humiditySilo2: (e: InputChg) => {
      const input = e.target.value;
      if (VALIDATION.ONLY_NUMS(input)) {
        rootDataError('humiditySilo2', 'Solo se aceptan números.');
        return;
      }

      if (!VALIDATION.BETWEEN_0_AND_100(input)) {
        rootDataError('humiditySilo2', 'Solo se aceptan números entre 0 y 100.');
        return;
      }

      rootDataError('humiditySilo2', '');
      setSilobagGrupalExtraction((prevData) => ({
        ...prevData,
        humiditySilo2: input,
      }));
    },
  };

  const handlerChangeTypeOfExtraction = (event: SyntheticEvent) => {
    const target = event.target as HTMLInputElement;
    setExtractionType(parseInt(target.value));
  };

  const calculateWeightIfNeeded = () => {
    const isSiloWeight1GT0 = parseWeight(silobagGrupalExtraction.weigthSilo1 as string) > 0;
    const isSiloWeight2GT0 = parseWeight(silobagGrupalExtraction.weigthSilo2 as string) > 0;
    const isValidExtractionWeight = isMultipleExtraction && (isSiloWeight1GT0 || isSiloWeight2GT0);

    if (!manualWeightInput) {
      if (isValidExtractionWeight) {
        const newWeight =
          parseWeight(silobagGrupalExtraction.weigthSilo1 as string) +
          parseWeight(silobagGrupalExtraction.weigthSilo2 as string);
        setExtractionValues((prevValues) => ({
          ...prevValues,
          weight: newWeight,
        }));
      } else if (isMultipleExtraction) {
        setExtractionValues((prevValues) => ({
          ...prevValues,
          weight: '',
        }));
      } else if (!extractionValues.extraction_date) {
        setExtractionValues((prevValues) => ({
          ...prevValues,
          weight: extractionValues.weight || '',
        }));
      }
    }

    //Refactorizar
  };

  useEffect(() => {
    calculateWeightIfNeeded();
  }, [silobagGrupalExtraction, isMultipleExtraction, manualWeightInput]);

  useEffect(() => {
    if (extractionValues.weight === '') {
      setManualWeightInput(false);
    }
  }, [extractionValues.weight]);

  const submitExtraction = (acceptInvalidation: boolean) => {
    const typeOfExtraction = extractionType === TRUCK_ID ? 'truck' : 'consolidated';
    const correctDateFormat = formatDate(extractionValues.extraction_date);
    const body = {
      ...extractionValues,
      extraction_date: correctDateFormat,
      weight: parseInt(extractionValues.weight as string, 10),
      gross_weight: parseInt(extractionValues.gross_weight as string, 10),
      truck_weight: parseInt(extractionValues.truck_weight as string, 10),
      type: typeOfExtraction,
      ...(type === 'new' ? { accept_protocol_invalidation: acceptInvalidation satisfies boolean } : {}),
      ...(pdfView && { ocr_run_id: importArcaSumm?.response.ocr_run_record.id }),
    };

    if (isMultipleExtraction) {
      const extractionDate = extractionValues.extraction_date;
      const [year, month, day] = (extractionDate as string).split('-');
      const formattedMonth = month.padStart(2, '0');
      const formattedDay = day.padStart(2, '0');
      const formattedDate = `${year}-${formattedMonth}-${formattedDay}`;
      const currentDate = new Date();
      const hours = String(currentDate.getUTCHours()).padStart(2, '0');
      const minutes = String(currentDate.getUTCMinutes()).padStart(2, '0');
      const seconds = String(currentDate.getUTCSeconds()).padStart(2, '0');
      const milliseconds = String(currentDate.getUTCMilliseconds()).padStart(3, '0');

      const multipleBody = {
        extraction_date: formattedDate + `T${hours}:${minutes}:${seconds}.${milliseconds}Z`,
        last_extraction: false,
        observations: extractionValues.observations!,
        weight: extractionValues.weight as string,
        gross_weight: extractionValues.gross_weight!,
        waybill: extractionValues.waybill!,
        truck_weight: extractionValues.truck_weight!,
        trailer_back_plate: extractionValues.trailer_back_plate!,
        freight_transport: extractionValues.freight_transport!,
        driver: extractionValues.driver!,
        destination: extractionValues.destination!,
        ctg_document: extractionValues.ctg_document!,
        contract: extractionValues.contract!,
        carrier: extractionValues.carrier!,
        back_plate: extractionValues.back_plate!,
        comercial_km: '0',
        comercial_price: '',
        type: typeOfExtraction,
        ...(type === 'new' ? { accept_protocol_invalidation: acceptInvalidation satisfies boolean } : {}),
        silobag_extractions: [
          {
            silobag_id: (idToUse as string[])[0],
            weight: silobagGrupalExtraction.weigthSilo1,
            gross_weight: extractionValues.gross_weight,
            truck_weight: extractionValues.truck_weight,
            humidity: !silobagGrupalExtraction.humiditySilo1
              ? undefined
              : silobagGrupalExtraction.humiditySilo1,
          },
          {
            silobag_id: (idToUse as string[])[1],
            weight: silobagGrupalExtraction.weigthSilo2,
            gross_weight: extractionValues.gross_weight,
            truck_weight: extractionValues.truck_weight,
            humidity: !silobagGrupalExtraction.humiditySilo2
              ? undefined
              : silobagGrupalExtraction.humiditySilo2,
          },
        ],
      };
      triggerMultipleExtraction({ body: multipleBody });
      return;
    }

    triggerExtraction({ query: idToUse as string, body: body as CreateExtractionBody });
  };

  const checkMonitoredSilobagsAndSubmit = async (acceptInvalidation: boolean) => {
    const selectedIds = getSelectedSilobagIds();

    if (type !== 'new' || acceptInvalidation || selectedIds.length === 0) {
      submitExtraction(acceptInvalidation);
      return;
    }

    try {
      const { summary } = await listMonitoredSilobagProtocols({ query: { ids: selectedIds } });
      const monitoredIds =
        summary?.filter((item) => item.protocol != null).map((item) => item.silobag_id) ?? [];

      if (monitoredIds.length > 0) {
        setDisableSave(false);
        setMonitoredSilobagsToAlert(monitoredIds.map((id) => getSilobagDisplayName(id)));
        setOpenMonitoredWarningModal(true);
        return;
      }

      submitExtraction(false);
    } catch {
      submitExtraction(false);
    }
  };

  const handleConfirmMonitoredExtraction = () => {
    setOpenMonitoredWarningModal(false);
    setProtocolInvalidationAccepted(true);
    setDisableSave(true);
    submitExtraction(true);
  };

  const handleSave = () => {
    setDisableSave(true);

    try {
      const errorMsg = i18n.t('app.must_field');
      let error = false;
      if (silobagName === '') ((error = true), rootDataError('silobag_name', errorMsg));
      if (extractionValues.weight === '') ((error = true), rootDataError('weight', errorMsg));
      if (extractionValues.extraction_date === '' || !extractionValues.extraction_date)
        ((error = true), rootDataError('extraction_date', errorMsg));

      if (isMultipleExtraction && !silobagGrupalExtraction.weigthSilo1)
        ((error = true), rootDataError('silo_groupal_extraction_1', errorMsg));
      if (isMultipleExtraction && !silobagGrupalExtraction.weigthSilo2)
        ((error = true), rootDataError('silo_groupal_extraction_2', errorMsg));

      if (error) {
        setDisableSave(false);
        return;
      }

      const parsedWeight = parseInt(extractionValues.weight as string, 10);

      if (remainingStock && +remainingStock < parsedWeight && !openWarningModal) {
        setDisableSave(false);
        setOpenWarningModal(true);
        return;
      }

      if (type !== 'new') {
        const typeOfExtraction = extractionType === TRUCK_ID ? 'truck' : 'consolidated';
        const correctDateFormat = formatDate(extractionValues.extraction_date);
        const body = {
          ...extractionValues,
          extraction_date: correctDateFormat,
          weight: parsedWeight,
          gross_weight: parseInt(extractionValues.gross_weight as string, 10),
          truck_weight: parseInt(extractionValues.truck_weight as string, 10),
          type: typeOfExtraction,
          ...(pdfView && { ocr_run_id: importArcaSumm?.response.ocr_run_record.id }),
        };
        triggerUpdateExtraction({ query: idToUse as number, body: body });
        return;
      }

      void checkMonitoredSilobagsAndSubmit(protocolInvalidationAccepted);
    } catch {
      setTypeSnackbar?.('error');
      setSnackbarMessage?.('Ocurrió un error al realizar la extracción.');
      setOpenSnackbar?.(true);
      setOpen(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      const idArray = Array.isArray(idToUse) ? idToUse : [idToUse];
      if (!idArray.length) {
        setRemainingStock && setRemainingStock(0);
        setIsMultipleExtraction(false);
        return;
      }

      // TODO: CHEQUEAR PROBLEMAS CON USESERVICE
      if (silobagExtraction.length === 2) {
        const { summary } = await silobagDataDetailService({ query: idArray[0].toString() });
        const { summary: summary2 } = await silobagDataDetailService({ query: idArray[1].toString() });

        const remainingStockInt = summary?.remaining_stock ? parseInt(summary.remaining_stock) : 0;
        const remainingStockInt2 = summary2?.remaining_stock ? parseInt(summary2.remaining_stock) : 0;

        setRemainingStock?.(remainingStockInt + remainingStockInt2);
        setIsMultipleExtraction(true);
      } else {
        const { summary } = await silobagDataDetailService({ query: idArray[0].toString() });
        if (summary?.remaining_stock) setRemainingStock?.(summary?.remaining_stock);
        setIsMultipleExtraction(false);
      }
    };

    fetchData();
  }, [idToUse]);

  useEffectOnUpdate(() => {
    if (errorInExtraction || errorInMultiExtraction) {
      if (
        // TODO: VOLVER CUANDO SE HAYA TIPADO ERROR
        (errorMsg as { data: { error: string } })?.data?.error ===
          'The extraction weight is more than twice the initial weight' ||
        (errorMultipleMsg as { data: { error: string } })?.data?.error ===
          'The extraction weight is more than twice the initial weight'
      ) {
        setErrorExtractionMsg(i18n.t('app.silobags_extractions.extraction_more_than_doble'));
      } else {
        setErrorExtractionMsg(i18n.t('app.administrator.error_unexpected'));
      }

      setDisableSave(false);
    }
  }, [errorInExtraction, errorInMultiExtraction]);

  const isEmptyArray = (arr?: unknown) => Array.isArray(arr) && arr.length === 0;
  const isEmptyString = (val?: unknown) => val === '' || val === undefined || val === null;
  const isZeroOrEmpty = (val?: string | number) => !val || parseInt(val as string) === 0;

  const validationButtonSave =
    !writePermission ||
    isLoading ||
    disableSave ||
    isEmptyArray(idToUse) ||
    isZeroOrEmpty(extractionValues.weight as string) ||
    isEmptyString(extractionValues.extraction_date);

  const handleUploadARCAFile = (event: ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;
    const hasFiles = files && files.length > 0;
    if (!hasFiles) return;

    const [file] = Array.from(files);
    setPdfView(file);
    importArcaPdf({ body: { inputFile: file } });
  };

  return (
    <Box>
      {errorInExtraction && <ToastAction message={errorExtractionMsg} severity="error" />}
      {errorInMultiExtraction && <ToastAction message={errorExtractionMsg} severity="error" />}
      {isErrorArcaPdf && <ToastAction message="El formato del documento es incompatible" severity="error" />}
      {isInvalidFileType ? (
        <ToastAction
          key="invalid-file-type"
          message="Formato Invalido"
          severity="error"
          onClose={() => setIsInvalidFileType(false)}
        />
      ) : null}
      <MyModal open={open} onClose={handleOnClose} pdfView={pdfView} isErrorArcaPdf={isErrorArcaPdf}>
        {isLoadingArcaData ? (
          <Box width={'100%'} marginLeft={'50%'} marginTop={'40%'} marginBottom={'60%'}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Stack
              direction={'row'}
              height={'100%'}
              spacing={1}
              width={'100%'}
              display={'flex'}
              justifyContent={'space-between'}
            >
              <Box width={'100%'}>
                <Box style={{ overflowY: 'auto' }}>
                  <Box>
                    <Typography variant="h5">
                      {i18n.t('app.silobags_extractions.extraction_silobag')}
                    </Typography>
                  </Box>
                  <Divider />
                  <Box marginTop={1}>
                    <Button
                      variant="outlined"
                      sx={{ marginRight: 1, borderRadius: '25px' }}
                      tabIndex={-1}
                      role={undefined}
                      component="label"
                      disabled={readonly || isMultipleExtraction || extractionType === CONSOLIDATED_ID}
                    >
                      + {i18n.t('app.silobags_extractions.upload_ARCA_pdf')}{' '}
                      <VisuallyHiddenInput type="file" onChange={handleUploadARCAFile} />
                    </Button>
                    {i18n.t('app.silobags_extractions.max_file_weigth')}
                  </Box>

                  <Stack direction={'row'} spacing={1} mt={'20px'}>
                    <FormControl fullWidth required size="small" error={Boolean(errorData.silobag_name)}>
                      <InputLabel>
                        {silobagExtraction && silobagExtraction.length === 0
                          ? i18n.t('app.silobags_extractions.choose_two_silobags')
                          : i18n.t('app.silobags_extractions.silos')}
                      </InputLabel>
                      <Select
                        multiple
                        error={Boolean(errorData.silobag_name)}
                        value={silobagExtraction}
                        label={
                          silobagExtraction && silobagExtraction.length === 0
                            ? i18n.t('app.silobags_extractions.choose_two_silobags')
                            : i18n.t('app.silobags_extractions.silos')
                        }
                        disabled={Boolean(siloName) || lockSilobagSelection}
                        onChange={HANDLERS.silobag_name}
                        required
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((value) => (
                              <Chip
                                key={value}
                                label={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <span>{getSilobagDisplayName(value)}</span>
                                    <MonitoredSilobagIcon
                                      silobagId={resolveSilobagId(value)}
                                      monitoredSilobagIds={monitoredSilobagIds}
                                    />
                                  </Box>
                                }
                              />
                            ))}
                          </Box>
                        )}
                      >
                        {isLoadingExportSilobagsList ? (
                          <CircularProgress />
                        ) : siloName ? (
                          <MenuItem value={siloName} key={siloName}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <span>{siloName}</span>
                              {typeof silobagOrExtractionId === 'string' ? (
                                <MonitoredSilobagIcon
                                  silobagId={silobagOrExtractionId}
                                  monitoredSilobagIds={monitoredSilobagIds}
                                />
                              ) : null}
                            </Box>
                          </MenuItem>
                        ) : (
                          silosForExtraction?.data
                            ?.filter((silobag) => silobag.write_permission)
                            .map((silobag) => (
                              <MenuItem key={silobag.id} value={silobag.id}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                  <span>{silobag.name}</span>
                                  <MonitoredSilobagIcon
                                    silobagId={silobag.id}
                                    monitoredSilobagIds={monitoredSilobagIds}
                                  />
                                </Box>
                              </MenuItem>
                            ))
                        )}
                      </Select>
                      {errorData.silobag_name ? (
                        <FormHelperText>{errorData.silobag_name}</FormHelperText>
                      ) : null}
                    </FormControl>
                    <FormControl
                      fullWidth
                      required
                      error={Boolean(errorData.extraction_date)}
                      variant="outlined"
                      size="small"
                      style={{ width: '95%' }}
                      sx={stylesInput}
                    >
                      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={locale}>
                        <Container style={{ width: '100%' }}>
                          <DatePicker
                            label={i18n.t('app.silobags_extractions.date_withdrawal')}
                            onChange={HANDLERS.extraction_date}
                            disabled={type === 'view' || type === 'edit'}
                            value={
                              extractionValues.extraction_date
                                ? dayjs.utc(extractionValues.extraction_date)
                                : null
                            }
                          />
                        </Container>
                      </LocalizationProvider>
                      {errorData.extraction_date ? (
                        <FormHelperText>{errorData.extraction_date}</FormHelperText>
                      ) : null}
                    </FormControl>
                  </Stack>
                  <FormControl>
                    <Stack direction={'row'} display={'flex'} alignItems={'center'} spacing={2}>
                      <FormLabel>{i18n.t('app.silobags_extractions.type_extraction')}</FormLabel>
                      <RadioGroup row defaultValue={extractionType}>
                        <FormControlLabel
                          value={TRUCK_ID}
                          control={<Radio disabled={type === 'view' || type === 'edit'} />}
                          onChange={handlerChangeTypeOfExtraction}
                          label={i18n.t('app.silobags_extractions.truck')}
                        />
                        <FormControlLabel
                          value={CONSOLIDATED_ID}
                          disabled={Boolean(pdfView) || type === 'view' || type === 'edit'}
                          control={<Radio disabled={type === 'view' || type === 'edit'} />}
                          onChange={handlerChangeTypeOfExtraction}
                          label={i18n.t('app.silobags_extractions.consolidated')}
                        />
                      </RadioGroup>
                    </Stack>
                  </FormControl>
                  {extractionType === TRUCK_ID ? (
                    <Truck
                      remainingStock={remainingStock as number}
                      errorData={errorData}
                      handlers={HANDLERS}
                      extractionValues={extractionValues}
                      readonly={readonly}
                      isMultiple={isMultipleExtraction}
                      multipleSilosData={silobagGrupalExtraction}
                      setMultipleSilosData={setSilobagGrupalExtraction}
                      monitoredSilobagIds={monitoredSilobagIds}
                    />
                  ) : (
                    <Consolidate
                      remainingStock={remainingStock as number}
                      errorData={errorData}
                      handlers={HANDLERS}
                      extractionValues={extractionValues}
                      readonly={readonly}
                      isMultiple={isMultipleExtraction}
                      multipleSilosData={silobagGrupalExtraction}
                      setMultipleSilosData={setSilobagGrupalExtraction}
                      monitoredSilobagIds={monitoredSilobagIds}
                    />
                  )}
                  <Stack direction={'column'} spacing={2} style={{ marginTop: '10px' }}>
                    <span>{i18n.t('app.silobags_extractions.others')}</span>
                    <FormControl fullWidth size="small">
                      <TextareaAutosize
                        disabled={readonly}
                        value={extractionValues.observations!}
                        onChange={HANDLERS.observations}
                        placeholder={i18n.t('app.observations')}
                        minRows={5}
                        maxRows={10}
                        style={{
                          resize: 'none',
                          fontFamily: 'IBM Plex Sans, sans-serif',
                          fontSize: '1rem',
                          maxWidth: '100%',
                          maxHeight: '100%',
                          borderRadius: '5px',
                        }}
                      />
                    </FormControl>
                  </Stack>
                </Box>
                <Box>
                  <Stack direction={'row'} flex={1} justifyContent={'end'} alignItems={'center'}>
                    <Button style={{ color: '#000' }} onClick={handleOnClose}>
                      {i18n.t('app.cancel')}
                    </Button>
                    {!fromHistory && active && (
                      <Button
                        style={{ color: '#fff' }}
                        variant="contained"
                        disabled={validationButtonSave}
                        sx={configButtonStylesContained}
                        onClick={readonly ? enableEditMode : handleSave}
                      >
                        {isLoading ? (
                          <CircularProgress size={24} color="inherit" />
                        ) : readonly ? (
                          `${i18n.t('app.edit')}`
                        ) : (
                          `${i18n.t('app.save')}`
                        )}
                      </Button>
                    )}
                  </Stack>
                </Box>
              </Box>

              {pdfView && !isErrorArcaPdf && (
                <Box width={'60%'}>
                  <PdfViewer pdfView={pdfView} />
                </Box>
              )}
              {openWarningModal && (
                <WarningModal open={openWarningModal} setOpen={setOpenWarningModal} handleSave={handleSave} />
              )}
              {openMonitoredWarningModal && (
                <MonitoredWarningExtractionModal
                  open={openMonitoredWarningModal}
                  setOpen={setOpenMonitoredWarningModal}
                  handleSave={handleConfirmMonitoredExtraction}
                  monitoredSilobagIds={monitoredSilobagsToAlert}
                />
              )}
            </Stack>
          </>
        )}
      </MyModal>
    </Box>
  );
}
// TODO: CAMBIAR VALORES ANY
type ExtractProps = {
  remainingStock: number;
  errorData: ErrorData;
  handlers: Handler;
  extractionValues: ExtractionValues;
  readonly: boolean;
  isMultiple: boolean;
  multipleSilosData: MultipleSilosDataProps;
  setMultipleSilosData: React.Dispatch<React.SetStateAction<MultipleSilosDataProps>>;
  monitoredSilobagIds: Set<string>;
};

function Truck({
  remainingStock,
  errorData,
  handlers: HANDLERS,
  extractionValues,
  readonly,
  isMultiple,
  multipleSilosData,
  setMultipleSilosData,
  monitoredSilobagIds,
}: ExtractProps) {
  const { trigger, summary: searchOptions } = useService(searchExtractionFieldsService, {
    onSuccess: () => (isLoadingRef.current = false),
  });
  const [fieldSearch, setFieldSearch] = useState('');
  const [textSearch, setTextSearch] = useState('');

  const [carrierSearch, setCarrierSearch] = useState('');
  const [ctgDocSearch, setCtgDocSearch] = useState('');
  const [waybillSearch, setWaybillSearch] = useState('');
  const [contractSearch, setContractSearch] = useState('');

  const handleSearchSubmit = () => {
    if (carrierSearch === '') return;
    trigger({ query: { field: fieldSearch, text: textSearch } });
  };

  const isLoadingRef = useRef(false);

  const debounceDelay = 350;

  useDebounce(handleSearchSubmit, debounceDelay, [
    carrierSearch,
    ctgDocSearch,
    waybillSearch,
    contractSearch,
  ]);

  const isPredictiveLoading =
    isLoadingRef.current && Boolean(carrierSearch || ctgDocSearch || waybillSearch || contractSearch);

  useEffect(() => {
    setCarrierSearch(extractionValues.carrier ?? '');
    setCtgDocSearch(extractionValues.ctg_document ?? '');
    setWaybillSearch(extractionValues.waybill ?? '');
    setContractSearch(extractionValues.contract ?? '');
  }, [extractionValues]);

  const createInputChgEvent = (value: string): InputChg => {
    return {
      target: {
        value,
      },
    } as InputChg;
  };

  return (
    <Box>
      <Box>
        {/* PESO CAMION */}
        <Typography variant="body1" gutterBottom>
          {i18n.t('app.silobags_extractions.weight_truck')}
        </Typography>
        <Stack direction="row" spacing={1}>
          <TextField
            disabled={readonly}
            size="small"
            label={i18n.t('app.silobags_extractions.gross_weight') + ' (Kg)'}
            onChange={HANDLERS.gross_weight}
            error={Boolean(errorData.gross_weight)}
            value={
              readonly && extractionValues?.gross_weight
                ? i18n.numberToDelimited(extractionValues?.gross_weight, {
                    delimiter: i18n.t('config.delimiter'),
                  })
                : extractionValues?.gross_weight
            }
            helperText={errorData.gross_weight}
            fullWidth
          />
          <TextField
            disabled={readonly}
            size="small"
            label="Tara"
            fullWidth
            onChange={HANDLERS.truck_weight}
            error={Boolean(errorData.truck_weight)}
            value={
              readonly && extractionValues?.truck_weight
                ? i18n.numberToDelimited(extractionValues?.truck_weight, {
                    delimiter: i18n.t('config.delimiter'),
                  })
                : extractionValues?.truck_weight
            }
            helperText={errorData.truck_weight}
          />

          <FormControl required error={Boolean(errorData.weight)} fullWidth>
            <TextField
              disabled={readonly}
              size="small"
              fullWidth
              label={i18n.t('app.silobags_extractions.net_weight') + ' (Kg)'}
              required
              error={Boolean(errorData.weight)}
              onChange={HANDLERS.weight}
              value={
                readonly
                  ? i18n.numberToDelimited(extractionValues.weight!, {
                      delimiter: i18n.t('config.delimiter'),
                    })
                  : extractionValues.weight
              }
            />
            <Typography variant="body2">
              {HOOKS.useLocale().t('temp.common.stock_in_field')} (Kg):{' '}
              {i18n.numberToDelimited(remainingStock, { delimiter: i18n.t('config.delimiter') })} Kg
            </Typography>
            {errorData.weight ? <FormHelperText>{errorData.weight}</FormHelperText> : null}
          </FormControl>
        </Stack>
        <Stack direction="row" spacing={1}>
          <TextField
            disabled={readonly}
            error={Boolean(errorData.humidity)}
            helperText={errorData.humidity}
            size="small"
            label={i18n.t('app.silobags_extractions.humidity')}
            fullWidth={false}
            onChange={HANDLERS.humidity}
            sx={{ display: isMultiple ? 'none' : 'block' }}
            value={
              readonly && extractionValues?.humidity
                ? i18n.numberToDelimited(extractionValues?.humidity, {
                    delimiter: i18n.t('config.delimiter'),
                  })
                : extractionValues?.humidity
            }
          />
        </Stack>
        {isMultiple && (
          <Box sx={{ width: '100%' }}>
            <MultipleExtraction
              multipleSilosData={multipleSilosData}
              setMultipleSilosData={setMultipleSilosData}
              errorData={errorData}
              handlers={HANDLERS}
              monitoredSilobagIds={monitoredSilobagIds}
            />
          </Box>
        )}
        {/* TRANSPORTE */}
        <Typography variant="body1" gutterBottom>
          {i18n.t('app.silobags_extractions.transport')}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Stack width={'100%'} spacing={1}>
            <TextField
              disabled={readonly}
              label={i18n.t('app.silobags_extractions.freight_broker')}
              size="small"
              fullWidth
              onChange={HANDLERS.freighTransport}
              value={extractionValues.freight_transport as string}
            />
            <TextField
              disabled={readonly}
              label={i18n.t('app.silobags_extractions.driver')}
              size="small"
              fullWidth
              onChange={HANDLERS.driver}
              value={extractionValues.driver}
            />
            <TextField
              disabled={readonly}
              label={i18n.t('app.silobags_extractions.chassis_attached')}
              size="small"
              fullWidth
              onChange={HANDLERS.trailerBackPlate}
              value={extractionValues.trailer_back_plate as string}
            />
          </Stack>
          <Stack width={'100%'} spacing={1}>
            <AutocompleteInput
              label={i18n.t('app.silobags_extractions.carrier')}
              options={searchOptions?.response ?? []}
              loading={isPredictiveLoading}
              onInnerSelect={(value: string) => {
                setTextSearch(value);
                HANDLERS.carrier(createInputChgEvent(value));
                setFieldSearch('carrier');
                setCarrierSearch(value);
              }}
              onInnerInputChange={(value: string) => {
                setTextSearch(value);
                HANDLERS.carrier(createInputChgEvent(value));
                setFieldSearch('carrier');
                setCarrierSearch(value);
              }}
              search={carrierSearch}
              setSearch={setCarrierSearch}
              disabled={readonly}
              width={400}
            />

            <TextField
              disabled={readonly}
              label="Patente Chasis"
              size="small"
              fullWidth
              onChange={HANDLERS.backPlate}
              value={extractionValues.back_plate as string}
            />
          </Stack>
        </Stack>
        {/* DOCUMENTO */}
        <Typography variant="body1" gutterBottom>
          Documento
        </Typography>
        <Stack direction="row" spacing={1}>
          <AutocompleteInput
            label={i18n.t('app.silobags_extractions.ctg')}
            options={searchOptions?.response ?? []}
            loading={isPredictiveLoading}
            onInnerSelect={(value: string) => {
              setTextSearch(value);
              HANDLERS.ctgDocument(createInputChgEvent(value));
              setFieldSearch('ctg_document');
              setCtgDocSearch(value);
            }}
            onInnerInputChange={(value: string) => {
              setTextSearch(value);
              HANDLERS.ctgDocument(createInputChgEvent(value));
              setFieldSearch('ctg_document');
              setCtgDocSearch(value);
            }}
            search={ctgDocSearch}
            setSearch={setCtgDocSearch}
            disabled={readonly}
            width={350}
          />
          <AutocompleteInput
            label={i18n.t('app.silobags_extractions.consignment_note')}
            options={searchOptions?.response ?? []}
            loading={isPredictiveLoading}
            onInnerSelect={(value: string) => {
              setTextSearch(value);
              HANDLERS.waybill(createInputChgEvent(value));
              setFieldSearch('waybill');
              setWaybillSearch(value);
            }}
            onInnerInputChange={(value: string) => {
              setTextSearch(value);
              HANDLERS.waybill(createInputChgEvent(value));
              setFieldSearch('waybill');
              setWaybillSearch(value);
            }}
            search={waybillSearch}
            setSearch={setWaybillSearch}
            disabled={readonly}
            width={400}
          />
        </Stack>
        {/* COMPRADOR */}
        <Typography variant="body1" gutterBottom>
          Comprador
        </Typography>
        <Stack direction={'row'} spacing={1}>
          <AutocompleteInput
            label={i18n.t('app.silobags_extractions.contract')}
            options={searchOptions?.response ?? []}
            loading={isPredictiveLoading}
            onInnerSelect={(value: string) => {
              setTextSearch(value);
              HANDLERS.contract(createInputChgEvent(value));
              setFieldSearch('contract');
              setContractSearch(value);
            }}
            onInnerInputChange={(value: string) => {
              setTextSearch(value);
              HANDLERS.contract(createInputChgEvent(value));
              setFieldSearch('contract');
              setContractSearch(value);
            }}
            search={contractSearch}
            setSearch={setContractSearch}
            disabled={readonly}
            width={350}
          />
          <TextField
            disabled={readonly}
            label="Destino (Comprador)"
            size="small"
            fullWidth
            onChange={HANDLERS.destination}
            value={extractionValues.destination}
          />
        </Stack>
      </Box>
    </Box>
  );
}

function Consolidate({
  remainingStock,
  errorData,
  handlers: HANDLERS,
  extractionValues,
  readonly,
  isMultiple,
  multipleSilosData,
  setMultipleSilosData,
  monitoredSilobagIds,
}: ExtractProps) {
  return (
    <Box>
      <Box sx={{ width: '100%' }}>
        <FormControl required error={Boolean(errorData.weight)} sx={{ width: '50%' }}>
          <TextField
            disabled={readonly}
            size="small"
            fullWidth
            label={i18n.t('app.silobags_extractions.gross_net_consolidated') + ' (Kg)'}
            required
            error={Boolean(errorData.weight)}
            onChange={HANDLERS.weight}
            value={
              readonly
                ? i18n.numberToDelimited(extractionValues.weight!, {
                    delimiter: i18n.t('config.delimiter'),
                  })
                : extractionValues.weight
            }
          />
          <Typography variant="body2">
            {HOOKS.useLocale().t('temp.common.stock_in_field')} (Kg):{' '}
            {i18n.numberToDelimited(remainingStock, { delimiter: i18n.t('config.delimiter') })} Kg
          </Typography>
          {errorData.weight ? <FormHelperText>{errorData.weight}</FormHelperText> : null}
        </FormControl>
        {isMultiple && (
          <MultipleExtraction
            multipleSilosData={multipleSilosData}
            setMultipleSilosData={setMultipleSilosData}
            handlers={HANDLERS}
            errorData={errorData}
            consolidated={true}
            monitoredSilobagIds={monitoredSilobagIds}
          />
        )}
      </Box>
    </Box>
  );
}

type MultipleExtractionProps = {
  handlers: Handler;
  errorData: ErrorData;
  multipleSilosData: MultipleSilosDataProps;
  setMultipleSilosData: React.Dispatch<React.SetStateAction<MultipleSilosDataProps>>;
  consolidated?: boolean;
  monitoredSilobagIds: Set<string>;
};

function MultipleExtraction(MEProps: MultipleExtractionProps) {
  const {
    multipleSilosData,
    handlers: HANDLERS,
    errorData,
    consolidated = false,
    monitoredSilobagIds,
  } = MEProps;

  return (
    <Box mt={2} mb={2} width={'100%'}>
      <Stack direction={'column'} spacing={2}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={'center'}
          justifyContent={'start'}
          spacing={2}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              width: '30%',
              overflow: 'hidden',
            }}
          >
            <Typography textOverflow={'ellipsis'} overflow={'hidden'} whiteSpace={'nowrap'}>
              {multipleSilosData?.nameSilo1}
            </Typography>
            {multipleSilosData?.idSilo1 ? (
              <MonitoredSilobagIcon
                silobagId={multipleSilosData.idSilo1}
                monitoredSilobagIds={monitoredSilobagIds}
              />
            ) : null}
          </Box>
          <FormControl required error={Boolean(errorData.silo_groupal_extraction_1)} sx={{ width: 270 }}>
            <TextField
              required
              label={i18n.t('app.silobags_extractions.enter_weight')}
              size="small"
              onChange={HANDLERS?.silo_group_1}
              error={Boolean(errorData.silo_groupal_extraction_1)}
              value={multipleSilosData.weigthSilo1}
            />
            {errorData.silo_groupal_extraction_1 ? (
              <FormHelperText>{errorData.silo_groupal_extraction_1}</FormHelperText>
            ) : null}
            {!consolidated && (
              <TextField
                label={i18n.t('app.silobags_extractions.humidity')}
                size="small"
                onChange={HANDLERS?.humiditySilo1}
                error={Boolean(errorData.humiditySilo1)}
                helperText={errorData.humiditySilo1}
                value={consolidated ? null : multipleSilosData.humiditySilo1}
                sx={{ marginTop: 1 }}
              />
            )}
          </FormControl>
        </Stack>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={'center'}
          justifyContent={'start'}
          spacing={2}
          width={'100%'}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              width: '30%',
              overflow: 'hidden',
            }}
          >
            <Typography textOverflow={'ellipsis'} overflow={'hidden'} whiteSpace={'nowrap'}>
              {multipleSilosData?.nameSilo2}
            </Typography>
            {multipleSilosData?.idSilo2 ? (
              <MonitoredSilobagIcon
                silobagId={multipleSilosData.idSilo2}
                monitoredSilobagIds={monitoredSilobagIds}
              />
            ) : null}
          </Box>
          <FormControl required error={Boolean(errorData.silo_groupal_extraction_2)} sx={{ width: 270 }}>
            <TextField
              required
              label={i18n.t('app.silobags_extractions.enter_weight')}
              size="small"
              onChange={HANDLERS?.silo_group_2}
              error={Boolean(errorData.silo_groupal_extraction_2)}
              value={multipleSilosData.weigthSilo2}
            />
            {errorData.silo_groupal_extraction_2 ? (
              <FormHelperText>{errorData.silo_groupal_extraction_2}</FormHelperText>
            ) : null}
            {!consolidated && (
              <TextField
                label={i18n.t('app.silobags_extractions.humidity')}
                size="small"
                onChange={HANDLERS?.humiditySilo2}
                error={Boolean(errorData.humiditySilo2)}
                helperText={errorData.humiditySilo2}
                value={consolidated ? null : multipleSilosData.humiditySilo2}
                sx={{ marginTop: 1 }}
              />
            )}
          </FormControl>
        </Stack>
      </Stack>
    </Box>
  );
}

function MyModal({ open, onClose, children, pdfView, isErrorArcaPdf }: PropsWithChildren<ModalProps>) {
  const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: pdfView && !isErrorArcaPdf ? 2000 : 750,
    maxHeight: '95vh',
    bgcolor: 'background.paper',
    borderRadius: 4,
    boxShadow: 24,
    p: 2,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    overflow: 'auto',
  };

  const handleClose = (_: never, reason: string) => {
    if (reason === 'backdropClick') return;
    onClose();
  };

  return (
    <Modal open={open} keepMounted onClose={handleClose}>
      <Fade in={open} appear={false} timeout={175}>
        <Box sx={style}>{children}</Box>
      </Fade>
    </Modal>
  );
}
